import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { findAnchor } from 'constants/stoAnchors'

/**
 * Retry budget while the page fills in (a section can resolve after the hash lands).
 * Counted in attempts rather than wall-clock: background tabs throttle timers, and a
 * clock-based deadline would expire before the page was ever looked at.
 */
const ANCHOR_RETRY_ATTEMPTS = 40
const ANCHOR_RETRY_INTERVAL_MS = 100

/** Target counts as landed within this many px of its scroll-margin offset. */
const ANCHOR_SETTLE_TOLERANCE_PX = 4
/** Consecutive in-place checks before we stop watching for layout shift. */
const ANCHOR_SETTLE_CHECKS = 3

/**
 * `#trading` -> `trading`. Returns '' for anything that is not a registered anchor,
 * so an unknown or stale fragment is ignored rather than scrolling somewhere odd.
 */
export const getAnchorIdFromHash = (hash: string | undefined | null): string => {
  const raw = (hash ?? '').replace(/^#/, '').trim()
  if (!raw) return ''
  let decoded = raw
  try {
    decoded = decodeURIComponent(raw)
  } catch {
    // Malformed escape sequence — fall back to the raw value.
  }
  return findAnchor(decoded) ? decoded : ''
}

/**
 * A section can be rendered twice, one copy per breakpoint (see Section `mirror`), so
 * the target is whichever copy is actually laid out, not just the first id match.
 */
const findAnchorTarget = (id: string): HTMLElement | null => {
  const candidates = [
    document.getElementById(id),
    ...document.querySelectorAll<HTMLElement>(`[data-sto-section="${CSS.escape(id)}"]`),
  ]
  return candidates.find((el) => el && el.getClientRects().length > 0) ?? null
}

/** Keys that scroll the page. Tab or typing must not cancel the landing scroll. */
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar'])

/**
 * Smooth scrolling is animation-driven and does NOTHING in a hidden tab — a deep link
 * opened in a background tab would silently never scroll. Jump instantly there, and
 * whenever the user has asked for reduced motion.
 */
const resolveScrollBehavior = (): ScrollBehavior => {
  if (typeof window === 'undefined') return 'auto'
  if (typeof document !== 'undefined' && document.hidden) return 'auto'
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return 'auto'
  return 'smooth'
}

/**
 * Scrolls to the section named by the URL hash.
 *
 * Needed because the hash usually arrives via a history replace (the legacy
 * `/:slug/<tab>` redirect) rather than a real navigation, and browsers do not
 * auto-scroll for those. Offset comes from the section's own `scroll-margin-top`.
 *
 * Retries while the page fills in, since a section may still be loading when the
 * hash is applied. Aborts as soon as the user scrolls — never fight them.
 *
 * Polls with setTimeout, not requestAnimationFrame: rAF does not fire in a hidden
 * tab, so a link opened in a background tab would never scroll.
 */
export function useStoAnchors(): void {
  const { hash } = useLocation()

  useEffect(() => {
    const id = getAnchorIdFromHash(hash)
    if (!id || typeof window === 'undefined') return

    let timer = 0
    let cancelled = false
    let attemptsLeft = ANCHOR_RETRY_ATTEMPTS

    const abort = () => {
      cancelled = true
      clearTimeout(timer)
    }

    const abortOnScrollKey = (event: KeyboardEvent) => {
      if (SCROLL_KEYS.has(event.key)) abort()
    }

    let settledChecks = 0

    /**
     * Scroll, then keep checking. Content above the target (banner, cookie bar, fonts,
     * sections resolving) can shift the layout after we land, which otherwise leaves the
     * reader somewhere other than the section they asked for. Re-assert until the target
     * holds its place, rather than trusting a single scroll.
     */
    const tick = () => {
      if (cancelled) return

      const target = findAnchorTarget(id)
      if (!target) {
        if (--attemptsLeft > 0) timer = window.setTimeout(tick, ANCHOR_RETRY_INTERVAL_MS)
        return
      }
      // A fixed target (the mobile KeyActions sheet) has nothing to scroll to; its owner opens it.
      if (window.getComputedStyle(target).position === 'fixed') return

      const offset = Number.parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0
      const drift = Math.abs(target.getBoundingClientRect().top - offset)

      if (drift <= ANCHOR_SETTLE_TOLERANCE_PX) {
        if (++settledChecks >= ANCHOR_SETTLE_CHECKS) return
      } else {
        settledChecks = 0
        target.scrollIntoView({ behavior: resolveScrollBehavior(), block: 'start' })
      }

      if (--attemptsLeft > 0) timer = window.setTimeout(tick, ANCHOR_RETRY_INTERVAL_MS)
    }

    window.addEventListener('wheel', abort, { passive: true, once: true })
    window.addEventListener('touchmove', abort, { passive: true, once: true })
    window.addEventListener('keydown', abortOnScrollKey)
    tick()

    return () => {
      cancelled = true
      clearTimeout(timer)
      window.removeEventListener('wheel', abort)
      window.removeEventListener('touchmove', abort)
      window.removeEventListener('keydown', abortOnScrollKey)
    }
  }, [hash])
}
